import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import type { VoiceConversationHistoryItemModel } from "../models/VoiceConversationHistory";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";

export type UseVoiceConversationHistoryParams = UsePaginatedRequestHookParams<{
  entityId: string;
  conversationId: string;
}>;

export type UseVoiceConversationHistoryReturn =
  UsePaginatedRequestHookReturn<VoiceConversationHistoryItemModel>;

export const useVoiceConversationHistory = ({
  entityId,
  conversationId,
  pageSize = 20,
  skip = false,
}: UseVoiceConversationHistoryParams): UseVoiceConversationHistoryReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [items, setItems] = useState<VoiceConversationHistoryItemModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchHistory] =
    integrationsApi.useLazyGetVoiceConversationHistoryQuery();

  const loadPage = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchHistory({
          entityId,
          convId: conversationId,
          page,
          page_size: pageSize,
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setItems((prev) => {
            if (page === 1) {
              return result.data;
            }

            const existingIds = new Set(prev.map((item) => item.mediaId));
            const newItems = result.data.filter(
              (item) => !existingIds.has(item.mediaId)
            );

            return [...prev, ...newItems];
          });

          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, entityId, fetchHistory, pageSize]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [currentPage, isLoading, loadPage, totalPages]);

  const reset = useCallback(() => {
    setItems([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && items.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, items.length, loadPage]);

  useEffect(() => {
    if (skip) return;
    reset();
  }, [conversationId, reset, skip]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && currentPage > 1;
  const isLoadingMoreError = isError && (currentPage > 1 || items.length === 0);

  return {
    data: items,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    reset,
    loadMore,
    retry,
  };
};
